import { Button, DialogActions, DialogContent, DialogProps, DialogTitle, Divider } from '@material-ui/core';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { ChipX } from 'framework/components/chips/ChipX';
import { ColorX } from 'framework/components/color/ColorX';
import { useThemeColor } from 'framework/components/color/useThemeColor';
import { DownloadMultipleIcon } from 'framework/components/icons/DownloadMultipleIcon';
import { InnerPageXWithStickySidebar } from 'framework/components/layout/InnerPageXWithStickySidebar';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { Typography16pxBold } from 'framework/components/typography/Typography16pxBold';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { DialogX } from 'framework/dialogs/DialogX';
import { BooleanRadioFilterComponent } from 'framework/filter/BooleanRadioFilterComponent';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { useAccordionState } from 'framework/utils/useAccordionState';
import {
	exactOnlineCostCentersQuery_all,
	exactOnlineCostCentersQuery_export,
	exactOnlineLocationsCommand_link,
	exactOnlineLocationsCommand_unlink,
	exactOnlineLocationsQuery_all,
	IExactOnlineCostCenter,
	IExactOnlineLocationDto,
} from 'gen/ApiClient';
import { useDownload } from 'gen/useDownload';
import { useLocalization } from 'localization/useLocalization';
import React, { useEffect, useMemo, useState } from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';
import { ExactOnlineCostCenterCardX, filterCostCenters } from './exact-online-cost-centers.components';

type ExactOnlineLocationsParams = {
	linked: boolean | undefined;
};

function filterLocations(locations: IExactOnlineLocationDto[], params: ExactOnlineLocationsParams) {
	return locations.filter(t => params.linked === undefined || t.linked === params.linked);
}

interface IProps extends IReloadTriggerProps {
	reload: VoidFunction;
}

export const ExactOnlineLocationsTab = ({ reload, reloadTrigger }: IProps) => {
	const strings = useLocalization();
	const [locations, reloadLocations] = useApiEffect(exactOnlineLocationsQuery_all);
	const [params, setParams] = useState<ExactOnlineLocationsParams>({ linked: undefined });
	const fLocations = useMemo(() => filterLocations(locations ?? [], params), [locations, params]);

	useLazyEffect(() => {
		reloadLocations();
	}, [reloadTrigger]);

	const [exportCostCenters, isExporting] = useDownload(exactOnlineCostCentersQuery_export);

	if (locations === undefined) {
		return <div></div>;
	}

	return (
		<InnerPageXWithStickySidebar
			maxWidth={1280}
			stickySidebar={
				<div
					className='df-col gap-16 mh100'
					style={{ padding: 16, paddingRight: 8, maxWidth: 400 }}>
					<CardX>
						<BooleanRadioFilterComponent
							label={`${strings.linked}?`}
							trueText={strings.linked}
							falseText={strings.notLinked}
							selected={params.linked}
							setSelected={value => setParams({ ...params, linked: value })}
							icon={<HelpOutlineIcon />}
						/>
					</CardX>
					<div className='fg1'></div>
					<Divider />
					<TextLinkButton
						startIcon={<DownloadMultipleIcon />}
						onClick={() => exportCostCenters()}
						disabled={isExporting}>
						{`Exporteer kostenplaats (.xlsx)`}
					</TextLinkButton>
				</div>
			}>
			<div
				className='df-col gap-16'
				style={{ padding: 16, paddingLeft: 8 }}>
				<CardX className='w100'>
					<div
						className='df-row'
						style={{ gap: 8 }}>
						<Typography16pxBold>{strings.results}</Typography16pxBold>
						<div className='fg1'></div>
					</div>
					<Divider style={{ marginTop: 8 }} />
					{fLocations.map(t => (
						<ExactOnlineLocationListItem
							key={t.id}
							item={t}
							reload={reload}
						/>
					))}
				</CardX>
			</div>
		</InnerPageXWithStickySidebar>
	);
};

const MinorFontSize = 13;

type ExactOnlineLocationListItemProps = {
	item: IExactOnlineLocationDto;
	withoutBorder?: boolean;
	reload: VoidFunction;
};

const ExactOnlineLocationListItem = ({ item, withoutBorder, reload }: ExactOnlineLocationListItemProps) => {
	const strings = useLocalization();
	const color = useMemo<ColorX>(() => (item.linked ? 'success' : item.isDeleted ? 'default' : 'error'), [item]);
	const tColor = useThemeColor(color);
	const { open, confirm, cancel } = useDialogsContext(reload);

	const onUnlink = () => {
		open(
			<AlertDialog
				open
				title={strings.deleteLink}
				content={strings.doYouWantToDeleteExistingLinkWithGLAccountQuestion}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => exactOnlineLocationsCommand_unlink({ locationId: item.id })}
				accept={confirm}
			/>
		);
	};

	const onLink = () => {
		open(
			<LinkExactOnlineLocationWithCostCenterForm
				open
				item={item}
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	return (
		<div
			className='df-row w100'
			style={{
				borderBottom: withoutBorder === true ? 'unset' : '1px solid rgba(224, 224, 224, 1)',
				padding: '12px 0px',
			}}>
			<div style={{ marginRight: 8, color: tColor, fontSize: 22 }}>
				<ExactOnlineLocationLinkIcon
					item={item}
					style={{ fontSize: 22, color: tColor }}
				/>
			</div>
			<div className='df-col gap-4 fg1'>
				<div className='df-row-ac'>
					<Typography16pxBold>{item.identifier}</Typography16pxBold>
				</div>
				<ChipX
					label={item.linked ? strings.linked : item.isDeleted ? strings.deleted : strings.notLinked}
					color={color}
					style={{ marginRight: 'auto' }}
				/>
				{item.linked && (
					<>
						<div
							className='df-row-ac'
							style={{ fontSize: MinorFontSize, gap: 4 }}>
							<span>{`${strings.exactOnlineGLAccountCode}:`}</span>
							<TextLinkButton
								style={{ fontSize: MinorFontSize }}
								onClick={() => {}}>
								{item.exactOnlineCostCenter?.code}
							</TextLinkButton>
						</div>
						<TextLinkButton
							hoverColor='secondary'
							startIcon={<LinkOffIcon />}
							onClick={onUnlink}>
							{strings.deleteLinkImperative}
						</TextLinkButton>
					</>
				)}
				{item.linked === false && (
					<TextLinkButton
						color='primary'
						startIcon={<LinkIcon />}
						onClick={onLink}>
						{strings.link}
					</TextLinkButton>
				)}
			</div>
		</div>
	);
};

type ExactOnlineLocationLinkIconProps = {
	item: IExactOnlineLocationDto;
	style?: React.CSSProperties;
};

export const ExactOnlineLocationLinkIcon = ({ item, style }: ExactOnlineLocationLinkIconProps) => {
	if (item.linked) {
		return <CheckCircleOutlineIcon style={style} />;
	} else if (item.isDeleted) {
		return <HighlightOffOutlinedIcon style={style} />;
	} else {
		return <BlockIcon style={style} />;
	}
};

type LinkExactOnlineLocationWithCostCenterFormProps = DialogProps & {
	item: IExactOnlineLocationDto;
	cancel: VoidFunction;
	confirm: () => void;
};

const LinkExactOnlineLocationWithCostCenterForm = ({ item, cancel, confirm, ...rest }: LinkExactOnlineLocationWithCostCenterFormProps) => {
	const strings = useLocalization();
	const [costCenters] = useApiEffect(exactOnlineCostCentersQuery_all);
	const [searchString, setSearchString] = useState<string>('');
	const fCostCenters = useMemo(() => filterCostCenters(costCenters ?? [], searchString), [costCenters, searchString]);
	const [selectedCostCenter, setSelectedCostCenter] = useAccordionState<IExactOnlineCostCenter | undefined>();
	const [submit] = useFormSubmit(exactOnlineLocationsCommand_link);
	const notify = useSnackbarNotify();

	useEffect(() => {
		setSelectedCostCenter(undefined);
		// eslint-disable-next-line
	}, [searchString]);

	const onSubmit = async () => {
		if (selectedCostCenter === undefined) {
			return;
		}

		const r = await submit({ locationId: item.id, exactOnlineCostCenterId: selectedCostCenter.id });
		if (r.isFailure) {
			notify(r.error);
		} else {
			confirm();
		}
	};

	return (
		<DialogX
			{...rest}
			open={rest.open}
			fullHeight>
			<DialogTitle>
				<div className='df-col gap-8'>
					<span>{`Link locatie met kostenplaats`}</span>
					<SearchbarX
						placeholder={`${strings.searchOnNameSlashCode}...`}
						onSearch={setSearchString}
						value={searchString}
					/>
				</div>
			</DialogTitle>
			<DialogContent
				className='df-col gap-8'
				dividers>
				{fCostCenters.map(t => (
					<ExactOnlineCostCenterCardX
						key={t.id}
						item={t}
						onSelect={() => setSelectedCostCenter(t)}
						isSelected={t.id === selectedCostCenter?.id}
					/>
				))}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={cancel}
					color='primary'>
					{strings.cancel}
				</Button>
				<Button
					onClick={onSubmit}
					color='primary'
					disabled={selectedCostCenter === undefined}>
					{strings.proceed}
				</Button>
			</DialogActions>
		</DialogX>
	);
};
